/**
 * VAT Settings Page (UI-7 v3 layout, BE-9 data)
 *
 * VAT registered toggle, TRN, rate, filing period and the month the quarters start in.
 * Saving once confirms the period layout (filing is refused until then); period fields lock after the first filing.
 * The P&L Recognition Basis and Small Business Relief are accounting settings (BE-10), saved by the same Save only when
 * they change, so changing them never confirms the VAT periods.
 * Route: /settings/vat
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Switch, Skeleton, MenuItem, Divider,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { can } from '../../utils/roleUtils';
import { vatReturnService } from '../../services/accounting/vatReturn.service';
import { profitLossService } from '../../services/accounting/profitLoss.service';
import { serverMessage } from '../../utils/apiConvert';

const QUARTER_STARTS = [
  [1, 'January (Jan–Mar, Apr–Jun, …)'],
  [2, 'February (Feb–Apr, May–Jul, …)'],
  [3, 'March (Mar–May, Jun–Aug, …)'],
];

const VAT_FIELDS = ['vatRegistered', 'trn', 'vatRate', 'periodMonths', 'firstPeriodMonth'];
const vatSnapshot = (s) => JSON.stringify(VAT_FIELDS.map((k) => String(s?.[k] ?? '')));

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const VatSettings = () => {
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [settings, setSettings] = useState(null);
  const [savedVat, setSavedVat] = useState('');
  const [acct, setAcct] = useState(null);
  const [savedAcct, setSavedAcct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const vat = vatReturnService.getVatSettings()
      .then((s) => {
        const loaded = { ...s, trn: s?.trn || '', vatRate: s?.vatRate ?? 5, periodMonths: s?.periodMonths || 3, firstPeriodMonth: s?.firstPeriodMonth || 1 };
        setSettings(loaded);
        setSavedVat(vatSnapshot(loaded));
      })
      .catch((err) => setError(serverMessage(err, 'Failed to load VAT settings')));
    const accounting = profitLossService.getSettings()
      .then((a) => {
        const loaded = { plBasis: a?.plBasis || 'ACCRUAL', sbrElected: Boolean(a?.sbrElected) };
        setAcct(loaded);
        setSavedAcct(loaded);
      })
      .catch((err) => setError(serverMessage(err, 'Failed to load accounting settings')));
    Promise.all([vat, accounting]).finally(() => setLoading(false));
  }, []);

  const set = (patch) => { setSettings((s) => ({ ...s, ...patch })); setSuccess(false); };
  const setAccounting = (patch) => { setAcct((a) => ({ ...a, ...patch })); setSuccess(false); };

  const acctChanged = Boolean(acct && savedAcct)
    && (acct.plBasis !== savedAcct.plBasis || acct.sbrElected !== savedAcct.sbrElected);

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    // VAT is saved unless the only change is the P&L part (a VAT save confirms the periods).
    const saveVat = vatSnapshot(settings) !== savedVat || !acctChanged;
    if (saveVat && settings.vatRegistered && settings.trn && settings.trn.length !== 15) {
      setError('TRN must be exactly 15 digits');
      return;
    }
    setSaving(true);
    const failed = [];
    if (saveVat) {
      try {
        const saved = await vatReturnService.updateVatSettings({
          vatRegistered: settings.vatRegistered,
          trn: settings.trn,
          vatRate: settings.vatRate,
          periodMonths: settings.periodMonths,
          firstPeriodMonth: settings.periodMonths === 3 ? settings.firstPeriodMonth : 1,
        });
        const next = { ...settings, ...saved, trn: saved?.trn || '' };
        setSettings(next);
        setSavedVat(vatSnapshot(next));
      } catch (err) {
        failed.push(`VAT settings: ${serverMessage(err, 'not saved')}`);
      }
    }
    if (acctChanged) {
      try {
        const saved = await profitLossService.updateSettings({ plBasis: acct.plBasis, sbrElected: acct.sbrElected });
        const next = { plBasis: saved?.plBasis || acct.plBasis, sbrElected: Boolean(saved?.sbrElected) };
        setAcct(next);
        setSavedAcct(next);
      } catch (err) {
        failed.push(`Profit & Loss settings: ${serverMessage(err, 'not saved')}`);
      }
    }
    if (failed.length) setError(failed.join(' · '));
    else setSuccess(true);
    setSaving(false);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton width="40%" height={32} />
        <Skeleton width="100%" height={200} sx={{ mt: 1 }} />
      </Box>
    );
  }
  if (!settings) {
    return <Box sx={{ p: 2 }}><Alert severity="error">{error || 'VAT settings are not available'}</Alert></Box>;
  }

  const registered = settings.vatRegistered !== false;
  const locked = Boolean(settings.periodsLocked);
  const disabled = !registered || !canWrite;

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(false)}>Settings saved.</Alert>}

      <Typography variant="h6" sx={{ fontSize: fs.md, fontWeight: fw.semibold, mb: 1.5 }}>VAT Settings</Typography>

      {registered && !settings.periodsConfirmed && (
        <Alert severity="warning" sx={{ mb: 1.5, maxWidth: 500 }}>
          Check the filing period and save once to confirm it. VAT returns cannot be filed until then.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, maxWidth: 500 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* VAT Registered */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>VAT Registered</Typography>
            <Switch checked={registered} onChange={(e) => set({ vatRegistered: e.target.checked })} size="small" disabled={!canWrite} />
          </Box>

          {/* TRN */}
          <TextField label="Tax Registration Number (TRN)" size="small" fullWidth value={settings.trn}
            onChange={(e) => set({ trn: e.target.value.replace(/\D/g, '').slice(0, 15) })}
            helperText="15-digit FTA registration number" disabled={disabled}
            InputProps={{ sx: { fontSize: fs.sm } }} InputLabelProps={{ sx: { fontSize: fs.sm } }} FormHelperTextProps={{ sx: { fontSize: fs.xs } }} />

          {/* VAT Rate */}
          <TextField label="VAT Rate (%)" size="small" type="number" fullWidth value={settings.vatRate}
            onChange={(e) => set({ vatRate: e.target.value })} disabled={disabled}
            InputProps={{ sx: { fontSize: fs.sm } }} InputLabelProps={{ sx: { fontSize: fs.sm } }} />

          {/* Period Type */}
          <FormControl disabled={disabled || locked}>
            <FormLabel sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>Filing Period</FormLabel>
            <RadioGroup row value={String(settings.periodMonths)} onChange={(e) => set({ periodMonths: Number(e.target.value) })}>
              <FormControlLabel value="3" control={<Radio size="small" />} label={<Typography sx={{ fontSize: fs.sm }}>Quarterly</Typography>} />
              <FormControlLabel value="1" control={<Radio size="small" />} label={<Typography sx={{ fontSize: fs.sm }}>Monthly</Typography>} />
            </RadioGroup>
          </FormControl>

          {/* Quarter alignment */}
          {settings.periodMonths === 3 && (
            <TextField select label="Quarters start in" size="small" fullWidth value={settings.firstPeriodMonth}
              onChange={(e) => set({ firstPeriodMonth: Number(e.target.value) })} disabled={disabled || locked}
              helperText="As on your FTA registration certificate"
              InputProps={{ sx: { fontSize: fs.sm } }} InputLabelProps={{ sx: { fontSize: fs.sm } }} FormHelperTextProps={{ sx: { fontSize: fs.xs } }}>
              {QUARTER_STARTS.map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: fs.sm }}>{l}</MenuItem>)}
            </TextField>
          )}
          {locked && (
            <Typography sx={{ fontSize: fs.xs, color: 'warning.main', mt: -1 }}>
              Cannot be changed after first period filed
            </Typography>
          )}

          {/* Profit & Loss — accounting settings, independent of VAT registration */}
          {acct && (
            <>
              <Divider />
              <FormControl disabled={!canWrite}>
                <FormLabel sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>Recognition Basis</FormLabel>
                <RadioGroup row value={acct.plBasis} onChange={(e) => setAccounting({ plBasis: e.target.value })}>
                  <FormControlLabel value="ACCRUAL" control={<Radio size="small" />} label={<Typography sx={{ fontSize: fs.sm }}>Accrual</Typography>} />
                  <FormControlLabel value="CASH" control={<Radio size="small" />} label={<Typography sx={{ fontSize: fs.sm }}>Cash</Typography>} />
                </RadioGroup>
                <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                  The official basis of your Profit &amp; Loss; the report can still show the other view
                </Typography>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: fs.sm, fontWeight: fw.medium }}>Small Business Relief elected</Typography>
                  <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                    Corporate tax estimate is 0 while yearly revenue stays within AED 3M
                  </Typography>
                </Box>
                <Switch checked={acct.sbrElected} onChange={(e) => setAccounting({ sbrElected: e.target.checked })} size="small" disabled={!canWrite} />
              </Box>
            </>
          )}

          {/* Save */}
          {canWrite && (
            <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving}
              sx={{ textTransform: 'none', fontSize: fs.sm, alignSelf: 'flex-start' }}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default VatSettings;
